import type { ChatMessage } from '@wundo/shared/chat.ts';
import type {
  EndRoleplayResponse,
  RoleplayMode,
  RoleplaySendResponse,
  RoleplaySessionResponse,
  VoiceConnection,
} from '@wundo/shared/roleplay.ts';
import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';

export type { RoleplaySessionResponse, VoiceConnection };

/**
 * One attempt, everything about it: what is being practised, how far along it
 * is, and — while a spoken one is running — how to join the call.
 *
 * The same endpoint the magic-link page has always used. Somebody signed in to
 * the app is the owner of their own sessions, which is the only thing it asks.
 */
export async function fetchRoleplaySession(
  sessionId: string,
): Promise<RoleplaySessionResponse> {
  return apiFetchJson<RoleplaySessionResponse>(`/roleplay-sessions/${sessionId}`);
}

/**
 * Begin the conversation in the chosen mode.
 *
 * A spoken one answers with the call to join; a written one answers with
 * nothing and the first message arrives over the socket.
 */
export async function beginRoleplaySession(
  sessionId: string,
  mode: RoleplayMode,
): Promise<VoiceConnection | null> {
  const response = await apiFetch(`/roleplay-sessions/${sessionId}/actions/start`, {
    method: 'POST',
    body: JSON.stringify({ mode }),
  });

  if (mode === 'chat') {
    return null;
  }

  const { voice } = (await response.json()) as { voice: VoiceConnection };
  return voice;
}

/**
 * End the conversation, or ask again for feedback that could not be written.
 *
 * Answers once the call's last words are written down: the feedback itself is
 * written in the background, and the session says `completing` until it is there. Ended before anybody spoke, it answers
 * `aborted` and there is none to wait for.
 */
export async function endRoleplaySession(
  sessionId: string,
): Promise<EndRoleplayResponse> {
  return apiFetchJson<EndRoleplayResponse>(
    `/roleplay-sessions/${sessionId}/actions/end`,
    { method: 'POST' },
  );
}

/**
 * Write to the character.
 *
 * The answer is the message as it was written down — the same message that
 * arrives on the session's room a moment later, under the same id, so the two
 * cannot show as two. What the character says back comes over the room alone.
 */
export async function sendRoleplayMessage(
  sessionId: string,
  text: string,
): Promise<ChatMessage> {
  const response = await apiFetchJson<RoleplaySendResponse>(
    `/roleplay-sessions/${sessionId}/messages`,
    { method: 'POST', body: JSON.stringify({ text }) },
  );

  return response.message;
}

/** How many messages are read at a time, going back through a conversation. */
export const ROLEPLAY_PAGE_SIZE = 50;

export interface RoleplayMessagePage {
  /** Oldest first, the way the conversation reads. */
  messages: ChatMessage[];
  /** The page before this one, or null at the beginning of the conversation. */
  older: number | null;
}

/**
 * One page of the conversation, oldest message first.
 *
 * Page 1 is the most recent, because that is the one being carried on; each
 * page after it goes further back. The API answers newest first for the same
 * reason, and the page is turned around here rather than on screen.
 */
export async function fetchRoleplayMessages(
  sessionId: string,
  page: number,
): Promise<RoleplayMessagePage> {
  const response = await apiFetchJson<{
    data: ChatMessage[];
    meta: { totalPages: number };
  }>(`/roleplay-sessions/${sessionId}/messages?page=${page}&limit=${ROLEPLAY_PAGE_SIZE}`);

  return {
    messages: [...response.data].reverse(),
    older: page < response.meta.totalPages ? page + 1 : null,
  };
}

/**
 * The whole conversation, oldest message first, for reading back once it is
 * over. Page after page until the beginning: a finished practice is a few dozen
 * messages, and the transcript is only worth reading whole.
 */
export async function fetchRoleplayTranscript(sessionId: string): Promise<ChatMessage[]> {
  const pages: ChatMessage[][] = [];
  let page: number | null = 1;

  while (page !== null) {
    const result: RoleplayMessagePage = await fetchRoleplayMessages(sessionId, page);
    pages.unshift(result.messages);
    page = result.older;
  }

  return pages.flat();
}
